"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.LineGraph = void 0;
const react_1 = __importDefault(require("react"));
const styled_components_1 = __importDefault(require("styled-components"));
const SVG_WIDTH = 520;
const LineGraphStyled = styled_components_1.default.div `
	width: 100%;
	flex: 1 1 auto;
	align-self: stretch;
	min-width: 0;
	display: block;
	overflow-x: auto;
	overflow-y: hidden;
	-webkit-overflow-scrolling: touch;

	.graph-inner {
		min-width: 100%;
	}

	svg {
		width: 100%;
		height: ${({ $height }) => `${$height}px`};
		display: block;
		overflow: visible;
	}

	.grid-line {
		stroke: #d9dde3;
		stroke-width: 1;
	}

	.axis-text,
	.point-label {
		fill: #7b7d84;
		font-family: 'Noto Sans KR', sans-serif;
	}

	.point-label {
		fill: #454552;
		font-weight: 700;
	}
`;
exports.LineGraph = react_1.default.memo(({ data, height = 170, maxValue = 100, yStep = 25, showPointLabel = true, lineColor = '#2f8eaa', axisFontSize = 10, pointLabelFontSize = 11, strokeWidth = 1.8, pointRadius = 2.6, edgeInset = 18, minPointSpacing = 44, }) => {
    const containerRef = react_1.default.useRef(null);
    const [containerWidth, setContainerWidth] = react_1.default.useState(SVG_WIDTH);
    react_1.default.useLayoutEffect(() => {
        const element = containerRef.current;
        if (!element)
            return;
        let frameId = 0;
        const updateWidth = () => {
            const ownWidth = element.getBoundingClientRect().width;
            const parentWidth = element.parentElement?.getBoundingClientRect().width ?? 0;
            const nextWidth = Math.max(Math.round(Math.max(ownWidth, parentWidth)), 320);
            setContainerWidth(prev => (prev === nextWidth ? prev : nextWidth));
        };
        updateWidth();
        frameId = window.requestAnimationFrame(updateWidth);
        if (typeof ResizeObserver !== 'undefined') {
            const observer = new ResizeObserver(() => {
                window.cancelAnimationFrame(frameId);
                frameId = window.requestAnimationFrame(updateWidth);
            });
            observer.observe(element);
            if (element.parentElement) {
                observer.observe(element.parentElement);
            }
            window.addEventListener('resize', updateWidth);
            window.addEventListener('orientationchange', updateWidth);
            return () => {
                window.cancelAnimationFrame(frameId);
                observer.disconnect();
                window.removeEventListener('resize', updateWidth);
                window.removeEventListener('orientationchange', updateWidth);
            };
        }
        window.addEventListener('resize', updateWidth);
        window.addEventListener('orientationchange', updateWidth);
        return () => {
            window.cancelAnimationFrame(frameId);
            window.removeEventListener('resize', updateWidth);
            window.removeEventListener('orientationchange', updateWidth);
        };
    }, [data.length, height]);
    const safeMaxValue = maxValue > 0 ? maxValue : 100;
    const padding = {
        top: showPointLabel ? 24 : 16,
        right: 18,
        bottom: 30,
        left: 42,
    };
    const desiredGraphWidth = padding.left +
        padding.right +
        edgeInset * 2 +
        Math.max(data.length, 1) * minPointSpacing;
    const svgWidth = Math.max(containerWidth, desiredGraphWidth);
    const chartWidth = svgWidth - padding.left - padding.right;
    const chartHeight = height - padding.top - padding.bottom;
    const drawableWidth = Math.max(chartWidth - edgeInset * 2, 0);
    const slotWidth = data.length > 0 ? drawableWidth / data.length : drawableWidth;
    const points = data.map((item, index) => {
        const x = data.length === 1
            ? padding.left + chartWidth / 2
            : padding.left +
                edgeInset +
                slotWidth * index +
                slotWidth / 2;
        const y = padding.top +
            chartHeight -
            (Math.max(0, Math.min(item.value, safeMaxValue)) / safeMaxValue) * chartHeight;
        return { ...item, x, y };
    });
    const path = points
        .map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`)
        .join(' ');
    const yTicks = [];
    for (let value = 0; value <= safeMaxValue; value += yStep) {
        yTicks.push(value);
    }
    if (yTicks[yTicks.length - 1] !== safeMaxValue) {
        yTicks.push(safeMaxValue);
    }
    return (react_1.default.createElement(LineGraphStyled, { "$height": height, ref: containerRef },
        react_1.default.createElement("div", { className: "graph-inner", style: { width: `${svgWidth}px` } },
            react_1.default.createElement("svg", { viewBox: `0 0 ${svgWidth} ${height}`, "aria-hidden": "true" },
                yTicks.map(value => {
                    const y = padding.top + chartHeight - (value / safeMaxValue) * chartHeight;
                    return (react_1.default.createElement("g", { key: value },
                        react_1.default.createElement("line", { className: "grid-line", x1: padding.left + edgeInset, y1: y, x2: svgWidth - padding.right - edgeInset, y2: y }),
                        react_1.default.createElement("text", { className: "axis-text", x: padding.left - 8, y: y + axisFontSize / 2 - 1, textAnchor: "end", fontSize: axisFontSize }, value)));
                }),
                points.length > 1 && (react_1.default.createElement("path", { d: path, fill: "none", stroke: lineColor, strokeWidth: strokeWidth, strokeLinejoin: "round", strokeLinecap: "round" })),
                points.map(point => (react_1.default.createElement("g", { key: point.id },
                    react_1.default.createElement("circle", { cx: point.x, cy: point.y, r: pointRadius, fill: lineColor }),
                    showPointLabel && (react_1.default.createElement("text", { className: "point-label", x: point.x, y: point.y - 10, textAnchor: "middle", fontSize: pointLabelFontSize }, `${point.value}%`)),
                    react_1.default.createElement("text", { className: "axis-text", x: point.x, y: height - 8, textAnchor: "middle", fontSize: axisFontSize }, point.label))))))));
});
